import type { DevicesDevice, DevicesDeviceType, DevicesAvailabilityState } from '@smarthome/api';

export type SortOption = 'name' | 'provider' | 'type' | 'availability' | 'lastUpdated';

export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'provider', label: 'Provider' },
  { value: 'type', label: 'Type' },
  { value: 'availability', label: 'Availability' },
  { value: 'lastUpdated', label: 'Last updated' },
];

/**
 * Filter devices by search query
 * Searches: name, humanId, provider, type
 */
export function filterBySearch(devices: DevicesDevice[], search: string): DevicesDevice[] {
  if (!search.trim()) return devices;

  const searchLower = search.toLowerCase();
  return devices.filter(
    (device) =>
      device.name?.toLowerCase().includes(searchLower) ||
      device.humanId?.toLowerCase().includes(searchLower) ||
      device.provider?.toLowerCase().includes(searchLower) ||
      device.type?.toLowerCase().includes(searchLower)
  );
}

/**
 * Filter devices by type
 */
export function filterByType(
  devices: DevicesDevice[],
  type: DevicesDeviceType | 'all'
): DevicesDevice[] {
  if (type === 'all') return devices;
  return devices.filter((device) => device.type === type);
}

/**
 * Filter devices by provider
 */
export function filterByProvider(
  devices: DevicesDevice[],
  provider: string | 'all'
): DevicesDevice[] {
  if (provider === 'all') return devices;
  return devices.filter((device) => device.provider === provider);
}

/**
 * Filter devices by favorites
 */
export function filterByFavorites(
  devices: DevicesDevice[],
  favoritesOnly: boolean
): DevicesDevice[] {
  if (!favoritesOnly) return devices;
  return devices.filter((device) => device.isFavorite);
}

/**
 * Sort devices by given option
 */
export function sortDevices(devices: DevicesDevice[], sortBy: SortOption): DevicesDevice[] {
  const sorted = [...devices];

  sorted.sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return (a.name || a.humanId || '').localeCompare(b.name || b.humanId || '');

      case 'provider':
        return (a.provider || '').localeCompare(b.provider || '');

      case 'type':
        return (a.type || '').localeCompare(b.type || '');

      case 'availability': {
        const order: Record<DevicesAvailabilityState, number> = {
          available: 0,
          unavailable: 1,
          unknown: 2,
        };
        const aOrder = a.availability ? (order[a.availability] ?? 3) : 3;
        const bOrder = b.availability ? (order[b.availability] ?? 3) : 3;
        return aOrder - bOrder;
      }

      case 'lastUpdated':
        return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();

      default:
        return 0;
    }
  });

  return sorted;
}

/**
 * Get unique device types from a list of devices
 */
export function getUniqueTypes(devices: DevicesDevice[]): DevicesDeviceType[] {
  const types = new Set<DevicesDeviceType>();
  for (const device of devices) {
    if (device.type) {
      types.add(device.type);
    }
  }
  return Array.from(types).sort();
}

/**
 * Get unique providers from a list of devices
 */
export function getUniqueProviders(devices: DevicesDevice[]): string[] {
  const providers = new Set<string>();
  for (const device of devices) {
    if (device.provider) {
      providers.add(device.provider);
    }
  }
  return Array.from(providers).sort();
}

/**
 * Apply all filters and sorting in one pass
 */
export function filterAndSortDevices(
  devices: DevicesDevice[],
  options: {
    search: string;
    type: DevicesDeviceType | 'all';
    provider: string | 'all';
    favoritesOnly: boolean;
    sortBy: SortOption;
  }
): DevicesDevice[] {
  let result = [...devices];

  // Apply filters
  result = filterByFavorites(result, options.favoritesOnly);
  result = filterBySearch(result, options.search);
  result = filterByType(result, options.type);
  result = filterByProvider(result, options.provider);

  // Apply sorting
  result = sortDevices(result, options.sortBy);

  return result;
}
